import {ReactComponent as ChangeIcon} from "../../core/ui/icon/Change.svg";

import "./_pool-price-info.scss";

import {ALGO_ASSET, convertFromBaseUnits} from "@tinymanorg/tinyman-js-sdk";
import {useState} from "react";
import classNames from "classnames";

import {SimpleAsset} from "../../asset/util/assetModels";
import {formatNumber} from "../../core/util/number/numberUtils";
import {MIN_POOL_PRICE_RATIO} from "./PoolPriceInfo.constants";
import Button from "../../component/button/Button";
import Typography from "../../component/typography/Typography";

enum PoolPriceInfoRatioOrder {
  STANDARD = "asset_1/asset_2",
  REVERSED = "asset_2/asset_1"
}

interface PoolPriceInfoProps {
  asset_1: Pick<SimpleAsset, "unit_name" | "decimals">;
  asset_1_reserves: number;
  asset_2: Pick<SimpleAsset, "unit_name" | "decimals">;
  asset_2_reserves: number;
  isDisabled?: boolean;
  customClassName?: string;
}

/**
 * Used for showing price data for a pair of assets, (e.g. "0.684133 USDC per ALGO")
 * and allowing user to toggle between the two ratios by clicking on it. (asset1/asset2 or asset2/asset1)
 */
function PoolPriceInfo({
  asset_1,
  asset_1_reserves,
  asset_2,
  asset_2_reserves,
  isDisabled,
  customClassName
}: PoolPriceInfoProps) {
  const [ratioOrder, setRatioOrder] = useState(PoolPriceInfoRatioOrder.REVERSED);

  return (
    <Button
      typography={"body"}
      customClassName={classNames(
        "is-vertically-centered",
        "color--text-gray",
        "font-weight--medium",
        "pool-price-info",
        customClassName
      )}
      onClick={toggleRatioOrder}
      isDisabled={isDisabled}>
      <ChangeIcon className={"pair-price-info-button__change-icon"} />

      <Typography fontWeight={"medium"} textColor={"text-gray"} as={"span"}>
        {renderRatio()}
      </Typography>
    </Button>
  );

  function renderRatio() {
    const decimalFormatter = formatNumber({
      maximumFractionDigits: ALGO_ASSET.decimals,
      minimumFractionDigits: 0
    });
    const reserves = {
      asset_1: convertFromBaseUnits(asset_1.decimals, asset_1_reserves),
      asset_2: convertFromBaseUnits(asset_2.decimals, asset_2_reserves)
    };
    let text;

    switch (ratioOrder) {
      case PoolPriceInfoRatioOrder.STANDARD: {
        const ratio = reserves.asset_1 / reserves.asset_2;

        text = `${
          ratio < MIN_POOL_PRICE_RATIO || isNaN(ratio) ? `≈ 0` : decimalFormatter(ratio)
        }  ${asset_1.unit_name} per ${asset_2.unit_name}`;
        break;
      }

      case PoolPriceInfoRatioOrder.REVERSED: {
        const ratio = reserves.asset_2 / reserves.asset_1;

        text = `${
          ratio < MIN_POOL_PRICE_RATIO || isNaN(ratio) ? `≈ 0` : decimalFormatter(ratio)
        }  ${asset_2.unit_name} per ${asset_1.unit_name}`;
        break;
      }

      default:
        break;
    }

    return text;
  }

  function toggleRatioOrder() {
    setRatioOrder((prevRatioOrder) =>
      prevRatioOrder === PoolPriceInfoRatioOrder.STANDARD
        ? PoolPriceInfoRatioOrder.REVERSED
        : PoolPriceInfoRatioOrder.STANDARD
    );
  }
}

export default PoolPriceInfo;
